import { useState } from 'react';
import { ClipboardList, SlidersHorizontal, X } from 'lucide-react';
import { useAuditLog, type AuditLogParams } from '../../api/auditLog';
import { useEngineerList } from '../../api/engineers';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import { TablePageSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDateTime } from '../../lib/dates';

const SELECT = 'h-8 rounded-md border-0 bg-transparent pl-2 pr-7 text-sm focus:outline-none focus:ring-0 cursor-pointer text-foreground';
const INPUT  = 'h-8 rounded-md border-0 bg-transparent px-2 text-sm focus:outline-none focus:ring-0 text-foreground placeholder:text-muted-foreground w-28';

function actionDotClass(action: string): string {
  const verb = action.split('.')[1] ?? '';
  if (/created|added|granted|imported|activated/.test(verb)) return 'bg-emerald-500';
  if (/updated|edited|changed|calibrated/.test(verb))        return 'bg-blue-500';
  if (/deleted|removed|revoked/.test(verb))                  return 'bg-red-500';
  if (/login|logout|password|locked/.test(action))           return 'bg-violet-500';
  if (/completed|done/.test(verb))                           return 'bg-gray-400';
  return 'bg-muted-foreground/30';
}

function formatAction(action: string): string {
  return action
    .split('.')
    .map((s) => s.replace(/_/g, ' '))
    .join(' · ');
}

function actorInitials(name: string): string {
  return name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
}

export default function AuditLogPage() {
  const [actorId, setActorId] = useState('');
  const [action,  setAction]  = useState('');
  const [from,    setFrom]    = useState('');
  const [to,      setTo]      = useState('');

  const { cursor, hasPrev, goNext, goPrev } = useCursorPagination<number>();
  const { data: engineers } = useEngineerList();

  const hasFilters = !!(actorId || action || from || to);
  const clearFilters = () => { setActorId(''); setAction(''); setFrom(''); setTo(''); };

  const params: AuditLogParams = {
    cursor:  cursor,
    limit:   50,
    actorId: actorId || undefined,
    action:  action  || undefined,
    from:    from    || undefined,
    to:      to      || undefined,
  };

  const { data, isLoading, error, refetch } = useAuditLog(params);

  return (
    <div>
      <PageHeader title="Audit log" />

      <div className="mb-4 flex items-center gap-2 rounded-xl border bg-card px-3 py-2 shadow-sm">
        <SlidersHorizontal className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="flex flex-1 flex-wrap items-center gap-0.5">
          <div className="flex items-center">
            <span className="px-2 text-xs font-medium text-muted-foreground">Actor</span>
            <select className={SELECT} value={actorId} onChange={(e) => setActorId(e.target.value)}>
              <option value="">All</option>
              {engineers?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div className="mx-1 h-4 w-px bg-border" />
          <div className="flex items-center">
            <span className="px-2 text-xs font-medium text-muted-foreground">Action</span>
            <input type="text" placeholder="e.g. task.created" value={action} onChange={(e) => setAction(e.target.value)} className={INPUT} />
          </div>
          <div className="mx-1 h-4 w-px bg-border" />
          <div className="flex items-center">
            <span className="px-2 text-xs font-medium text-muted-foreground">From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={INPUT} />
          </div>
          <div className="mx-1 h-4 w-px bg-border" />
          <div className="flex items-center">
            <span className="px-2 text-xs font-medium text-muted-foreground">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={INPUT} />
          </div>
        </div>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {isLoading && <TablePageSkeleton cols={5} hasAction={false} />}
      {error     && <ErrorState onRetry={refetch} />}

      {!isLoading && !error && (
        <>
          {data?.items.length === 0 ? (
            <Card>
              <EmptyState
                icon={ClipboardList}
                title={hasFilters ? 'No entries match your filters' : 'No audit log entries yet'}
                description={hasFilters ? 'Try adjusting or clearing your filters.' : 'System actions will appear here as they occur.'}
                action={hasFilters ? <button onClick={clearFilters} className="text-xs text-primary hover:underline">Clear filters</button> : undefined}
              />
            </Card>
          ) : (
            <Card>
              <div className="flex items-center border-b px-4 py-2.5">
                <span className="text-xs text-muted-foreground">
                  {(data?.items.length ?? 0) >= 50 ? '50+ entries' : `${data?.items.length} entr${data?.items.length === 1 ? 'y' : 'ies'}`}
                  {hasFilters && <span className="ml-1 text-primary">· filtered</span>}
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">Time</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead className="w-44">Actor</TableHead>
                    <TableHead>Detail</TableHead>
                    <TableHead className="w-32">IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.items.map((entry) => {
                    const actor = engineers?.find((e) => e.id === entry.actorId);
                    const name = actor?.name ?? entry.actorId.slice(0, 8);
                    return (
                      <TableRow key={entry.id}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {formatDateTime(entry.ts)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 shrink-0 rounded-full ${actionDotClass(entry.action)}`} />
                            <span className="text-sm text-foreground capitalize">{formatAction(entry.action)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                              {actorInitials(name)}
                            </div>
                            <span className="text-sm text-muted-foreground">{name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground" title={entry.detail ?? undefined}>
                          {entry.detail ?? '—'}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{entry.ipAddress ?? '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}

          {(hasPrev || data?.hasMore) && (
            <div className="mt-3 flex items-center justify-end gap-1">
              <Button variant="ghost" size="sm" disabled={!hasPrev} onClick={goPrev}>← Previous</Button>
              <Button
                variant="ghost" size="sm"
                disabled={!data?.hasMore}
                onClick={() => data?.nextCursor != null && goNext(data.nextCursor)}
              >
                Next →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
